<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import KanbanRoleOption from './KanbanRoleOption.vue';
import {
  KANBAN_ACCESS_PERMISSIONS,
  KANBAN_ANALYTICS_BLOCK_PERMISSIONS,
  KANBAN_DEAL_MANAGE_PERMISSIONS,
  KANBAN_MANAGE_PERMISSIONS,
  KANBAN_OWN_DEALS_ONLY_PERMISSIONS,
} from 'dashboard/constants/permissions';

// Aba Kanban do modal de papéis. Eram 5 toggles soltos — 3 degraus de uma
// escada (ligar "Gerenciar" já valia "Ver", mas o toggle de "Ver" seguia
// desligado) e 2 restrições negativas que só faziam sentido com um degrau
// ligado. Feedback da A4 (conta 164, 2026-10-15): "ficou confuso e redundante".
// Agora são 3 perguntas com uma resposta cada; o que se salva continua sendo
// as MESMAS permissões em `permissions` (backend: KanbanPermissions).
const props = defineProps({
  // Lista completa de permissões do papel; esta aba só mexe nas do Kanban.
  modelValue: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const LEVELS = [
  { value: null, key: 'NONE' },
  { value: KANBAN_ACCESS_PERMISSIONS, key: 'VIEW' },
  { value: KANBAN_DEAL_MANAGE_PERMISSIONS, key: 'DEALS' },
  { value: KANBAN_MANAGE_PERMISSIONS, key: 'MANAGE' },
];
const LADDER = LEVELS.map(level => level.value).filter(Boolean);
const KANBAN_ALL = [
  ...LADDER,
  KANBAN_ANALYTICS_BLOCK_PERMISSIONS,
  KANBAN_OWN_DEALS_ONLY_PERMISSIONS,
];

const SCOPES = [
  { value: 'all', key: 'ALL' },
  { value: 'own', key: 'OWN' },
];

const has = permission => props.modelValue.includes(permission);

// Papel salvo pela tela antiga pode ter mais de um degrau: vale o mais alto,
// que é o que o backend já resolvia (IMPLIED_BY). Qualquer mexida na aba
// regrava a escada com esse degrau só (ver emitWith); abrir e salvar sem
// tocar no Kanban mantém o papel como estava — mesma permissão efetiva.
const level = computed(
  () => [...LADDER].reverse().find(permission => has(permission)) || null
);
const ownOnly = computed(() => has(KANBAN_OWN_DEALS_ONLY_PERMISSIONS));
const analyticsVisible = computed(
  () => !ownOnly.value && !has(KANBAN_ANALYTICS_BLOCK_PERMISSIONS)
);
const scopeSelected = value => (value === 'own') === ownOnly.value;

const emitWith = (add, remove, nextLevel = level.value) => {
  const kept = props.modelValue.filter(
    p => !remove.includes(p) && !LADDER.includes(p)
  );
  const ladder = nextLevel ? [nextLevel] : [];
  emit('update:modelValue', [...new Set([...kept, ...ladder, ...add])]);
};

// Sem acesso limpa as restrições junto: elas não significam nada sem degrau.
const setLevel = value =>
  value ? emitWith([], [], value) : emitWith([], KANBAN_ALL, null);

const setScope = value =>
  value === 'own'
    ? emitWith([KANBAN_OWN_DEALS_ONLY_PERMISSIONS], [])
    : emitWith([], [KANBAN_OWN_DEALS_ONLY_PERMISSIONS]);

const setAnalytics = visible =>
  visible
    ? emitWith([], [KANBAN_ANALYTICS_BLOCK_PERMISSIONS])
    : emitWith([KANBAN_ANALYTICS_BLOCK_PERMISSIONS], []);
</script>

<template>
  <div class="flex flex-col gap-5">
    <p class="text-sm text-n-slate-11">
      {{ t('CUSTOM_ROLE.FORM.KANBAN.DESCRIPTION') }}
    </p>

    <fieldset class="flex flex-col gap-2">
      <legend class="mb-2 text-sm font-medium text-n-slate-12">
        {{ t('CUSTOM_ROLE.FORM.KANBAN.LEVEL.LABEL') }}
      </legend>
      <div role="radiogroup" class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <KanbanRoleOption
          v-for="option in LEVELS"
          :key="option.key"
          :data-kanban-level="option.value || 'none'"
          :selected="level === option.value"
          :label="t(`CUSTOM_ROLE.FORM.KANBAN.LEVEL.${option.key}`)"
          :hint="t(`CUSTOM_ROLE.FORM.KANBAN.LEVEL.${option.key}_HINT`)"
          @select="setLevel(option.value)"
        />
      </div>
    </fieldset>

    <template v-if="level">
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('CUSTOM_ROLE.FORM.KANBAN.SCOPE.LABEL') }}
        </legend>
        <div role="radiogroup" class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <KanbanRoleOption
            v-for="option in SCOPES"
            :key="option.value"
            :data-kanban-scope="option.value"
            :selected="scopeSelected(option.value)"
            :label="t(`CUSTOM_ROLE.FORM.KANBAN.SCOPE.${option.key}`)"
            :hint="t(`CUSTOM_ROLE.FORM.KANBAN.SCOPE.${option.key}_HINT`)"
            @select="setScope(option.value)"
          />
        </div>
      </fieldset>

      <div
        class="flex items-start justify-between gap-3 pt-4 border-t border-n-weak"
      >
        <span class="flex flex-col min-w-0 gap-0.5">
          <label
            id="kanban-analytics-label"
            for="kanban-analytics"
            class="text-sm font-medium leading-5 text-n-slate-12"
            :class="ownOnly ? 'cursor-default' : 'cursor-pointer'"
          >
            {{ t('CUSTOM_ROLE.FORM.KANBAN.ANALYTICS.LABEL') }}
          </label>
          <span class="text-xs leading-snug text-n-slate-11">
            {{
              ownOnly
                ? t('CUSTOM_ROLE.FORM.KANBAN.ANALYTICS.OWN_ONLY_HINT')
                : t('CUSTOM_ROLE.FORM.KANBAN.ANALYTICS.HINT')
            }}
          </span>
        </span>
        <Switch
          id="kanban-analytics"
          aria-labelledby="kanban-analytics-label"
          data-kanban-analytics
          class="mt-0.5"
          :disabled="ownOnly"
          :model-value="analyticsVisible"
          @update:model-value="setAnalytics"
        />
      </div>
    </template>
  </div>
</template>
